import React, { useState, useEffect } from 'react';
import '../../styles/AdminDashboard.scss';
import { cmsAPI, getImageUrl } from '../../utils/api';
import { FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaTimes, FaFacebook, FaInstagram, FaTwitter, FaLinkedin } from 'react-icons/fa';

const AdminTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ id: null, name: '', role: '', facebook: '', instagram: '', twitter: '', linkedin: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchTeam = async () => {
    try {
      const res = await cmsAPI.getTeam();
      if (res.data.success) setTeam(res.data.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    data.append('name', formData.name);
    data.append('role', formData.role);
    if (formData.facebook) data.append('facebook', formData.facebook);
    if (formData.instagram) data.append('instagram', formData.instagram);
    if (formData.twitter) data.append('twitter', formData.twitter);
    if (formData.linkedin) data.append('linkedin', formData.linkedin);
    if (formData.image) data.append('image', formData.image);

    try {
      let res;
      if (isEditing) {
        res = await cmsAPI.updateTeam(formData.id, data);
      } else {
        res = await cmsAPI.createTeam(data);
      }

      if (res.data.success) {
        fetchTeam();
        resetForm();
        alert('Team member saved successfully!');
      } else {
        alert('Failed to save team member');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('An error occurred while saving.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    try {
      const res = await cmsAPI.deleteTeam(id);
      if (res.data.success) {
        fetchTeam();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleEdit = (member) => {
    setFormData({ 
      id: member.id, 
      name: member.name, 
      role: member.role, 
      facebook: member.facebook || '', 
      instagram: member.instagram || '', 
      twitter: member.twitter || '', 
      linkedin: member.linkedin || '',
      image: null 
    });
    setImagePreview(getImageUrl(member.image));
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({ id: null, name: '', role: '', facebook: '', instagram: '', twitter: '', linkedin: '', image: null });
    setImagePreview(null);
    setIsEditing(false);
  };

  return (
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <h1>CMS - Team</h1>
        <p>Manage your professional team members and their profiles</p>
      </div>

      <div className="admin-card">
        <h3>{isEditing ? <><FaEdit /> Edit Team Member</> : <><FaPlus /> Add New Member</>}</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" placeholder="e.g. John Doe" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Role / Position</label>
              <input type="text" placeholder="e.g. Senior Developer" required value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label><FaFacebook /> Facebook URL</label>
              <input type="url" placeholder="https://facebook.com/..." value={formData.facebook} onChange={(e) => setFormData({...formData, facebook: e.target.value})} />
            </div>
            <div className="form-group">
              <label><FaInstagram /> Instagram URL</label>
              <input type="url" placeholder="https://instagram.com/..." value={formData.instagram} onChange={(e) => setFormData({...formData, instagram: e.target.value})} />
            </div>
            <div className="form-group">
              <label><FaTwitter /> Twitter URL</label>
              <input type="url" placeholder="https://twitter.com/..." value={formData.twitter} onChange={(e) => setFormData({...formData, twitter: e.target.value})} />
            </div>
            <div className="form-group">
              <label><FaLinkedin /> LinkedIn URL</label>
              <input type="url" placeholder="https://linkedin.com/in/..." value={formData.linkedin} onChange={(e) => setFormData({...formData, linkedin: e.target.value})} />
            </div>
          </div>

          <div className="form-group">
            <label>Profile Picture</label>
            <div className="file-input-wrapper">
              <FaCloudUploadAlt size={30} color="#4318ff" />
              <p style={{ margin: '10px 0 0 0', color: '#a3aed0', fontSize: '14px' }}>
                {formData.image ? formData.image.name : 'Upload a professional headshot'}
              </p>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              
              {imagePreview && (
                <div className="file-preview">
                  <img src={imagePreview} alt="Preview" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }} />
                  <div className="remove-file" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
                    <FaTimes />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : isEditing ? 'Update Profile' : 'Add to Team'}
            </button>
            {isEditing && (
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-grid">
        {loading ? (
          <p>Loading team members...</p>
        ) : team.map(member => (
          <div key={member.id} className="item-card" style={{ textAlign: 'center' }}>
            <div style={{ padding: '30px 20px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img 
                src={getImageUrl(member.image) || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150"><rect width="100%" height="100%" fill="%231e293b"/><circle cx="75" cy="60" r="30" fill="%2364748b"/><path d="M25,127 C25,97 55,90 75,90 C95,90 125,97 125,127" fill="%2364748b"/></svg>'} 
                alt={member.name} 
                style={{ width: '100px', height: '100px', borderRadius: '24px', objectFit: 'cover', marginBottom: '20px', boxShadow: '0 8px 16px rgba(67, 24, 255, 0.15)' }} 
              />
              <h4 style={{ margin: '0 0 5px 0' }}>{member.name}</h4>
              <p style={{ margin: '0 0 15px 0', fontSize: '13px', fontWeight: 600, color: '#4318ff' }}>{member.role}</p>
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                {member.facebook && <FaFacebook color="#1877f2" />}
                {member.instagram && <FaInstagram color="#e4405f" />}
                {member.twitter && <FaTwitter color="#1da1f2" />}
                {member.linkedin && <FaLinkedin color="#0077b5" />}
              </div>

              <div className="card-footer" style={{ width: '100%' }}>
                <button className="btn-edit" onClick={() => handleEdit(member)}><FaEdit /> Edit</button>
                <button className="btn-delete" onClick={() => handleDelete(member.id)}><FaTrash /> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminTeam;
